import { Language } from '@mui/icons-material';
import { Box, Chip, Menu, MenuItem, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { settingsSectionBodyInsetSx } from './settingsSectionLayout';
import { LanguagePreference, normalizeLanguageCode } from '../../../utils/language-config';

const languages: Array<{ code: LanguagePreference; name: ReactNode }> = [
  { code: 'browser', name: <Trans i18nKey="language.browser">Browser Language</Trans> },
  { code: 'de', name: 'Deutsch' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'fr-CA', name: 'Français (Canada)' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt-BR', name: 'Português (Brasil)' },
];

interface LanguageSelectorProps {
  currentLanguage: string;
  onLanguageChange: (languageCode: LanguagePreference) => void;
  languagePreference?: LanguagePreference;
}

const LanguageSelector = ({ currentLanguage, onLanguageChange, languagePreference }: LanguageSelectorProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Determine which language should be shown as selected
  // If the stored preference is "browser", show "Browser Language"
  // Otherwise, show the specific language
  const displayLanguage = languagePreference === 'browser' ? 'browser' : normalizeLanguageCode(currentLanguage);
  const currentLanguageData = languages.find((lang) => lang.code === displayLanguage) || languages[2]; // Default to English

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (languageCode: LanguagePreference) => {
    onLanguageChange(languageCode);
    handleClose();
  };

  return (
    <Box sx={settingsSectionBodyInsetSx}>
      <Chip
        icon={<Language />}
        label={currentLanguageData.name}
        onClick={handleClick}
        variant="outlined"
        sx={{
          borderRadius: '20px',
          height: '40px',
          fontSize: '16px',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '& .MuiChip-icon': {
            fontSize: '20px',
          },
        }}
        aria-label={t('aria.select_language', 'Select language')}
        aria-expanded={open}
        aria-haspopup="true"
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 'min(320px, calc(100dvh - 48px))',
              width: '250px',
              borderRadius: '12px',
              mb: 1,
            },
          },
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageSelect(language.code)}
            selected={language.code === displayLanguage}
            sx={{
              borderRadius: '8px',
              margin: '4px 8px',
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: language.code === displayLanguage ? 600 : 400 }}>
              {language.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSelector;
